import { useProtocolRevenue } from '@/hooks/useProtocolRevenue'
import { usePlatformActivity } from '@/hooks/usePlatformActivity'
import { useUserStats } from '@/hooks/useDashboardData'
import { useInfrastructureStats } from '@/hooks/useInfrastructureStats'
import { useStakingHealth } from '@/hooks/useStakingHealth'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { formatNumber } from '@/lib/utils'
import {
  DollarSign,
  TrendingUp,
  Rocket,
  GraduationCap,
  Users,
  Activity,
  BarChart3,
  Key,
  CheckCircle,
  UserCheck,
  Coins,
  Landmark,
  Clock,
  Zap,
  Smartphone,
  Share2,
} from 'lucide-react'

export function InvestorDashboard() {
  const { data: revenue, isLoading: revenueLoading } = useProtocolRevenue('30d')
  const { data: activity, isLoading: activityLoading } = usePlatformActivity('30d')
  const { data: users, isLoading: usersLoading } = useUserStats('30d')
  const { data: infra, isLoading: infraLoading } = useInfrastructureStats()
  const { data: staking, isLoading: stakingLoading } = useStakingHealth()

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Investor Dashboard</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1">
          Key metrics and platform health overview
        </p>
      </div>

      {/* Section 1: Key Metrics */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-muted-foreground">Key Metrics</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title="Total Protocol Revenue"
            value={revenue ? `${formatNumber(revenue.totalSOL)} SOL` : '--'}
            icon={DollarSign}
            variant="hero"
            loading={revenueLoading}
          />
          <StatsCard
            title="Protocol Revenue (30d)"
            value={revenue ? `${formatNumber(revenue.periodRevenueSOL)} SOL` : '--'}
            icon={DollarSign}
            loading={revenueLoading}
          />
          <StatsCard
            title="Revenue Growth Rate"
            value={revenue ? `${revenue.growthRatePercent.toFixed(1)}%` : '--'}
            icon={TrendingUp}
            loading={revenueLoading}
            change={revenue ? {
              value: revenue.growthRatePercent,
              trend: revenue.growthRatePercent >= 0 ? 'up' : 'down',
              suffix: '%',
              label: 'vs previous period',
            } : undefined}
          />
          <StatsCard
            title="Total Tokens Launched"
            value={activity ? formatNumber(activity.totalTokens) : '--'}
            icon={Rocket}
            loading={activityLoading}
          />
          <StatsCard
            title="Graduation Rate"
            value={activity ? `${activity.graduationRate.toFixed(1)}%` : '--'}
            icon={GraduationCap}
            loading={activityLoading}
          />
          <StatsCard
            title="Monthly Active Users"
            value={users ? formatNumber(users.activeUsers30d) : '--'}
            icon={Users}
            loading={usersLoading}
          />
          <StatsCard
            title="DAU/MAU Ratio"
            value={users ? `${users.dauMauRatio.toFixed(1)}%` : '--'}
            icon={Activity}
            loading={usersLoading}
          />
          <StatsCard
            title="Total Trading Volume"
            value={activity ? `${formatNumber(activity.totalTradingVolumeSOL)} SOL` : '--'}
            icon={BarChart3}
            loading={activityLoading}
          />
        </div>
      </section>

      {/* Section 2: Platform Health */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-muted-foreground">Platform Health</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatsCard
            title="Vanity Mints Available"
            value={infra ? infra.vanityAvailable.toLocaleString() : '--'}
            icon={Key}
            loading={infraLoading}
          />
          <StatsCard
            title="Verified Coins"
            value={infra ? infra.verifiedTokens.toLocaleString() : '--'}
            icon={CheckCircle}
            loading={infraLoading}
          />
          <StatsCard
            title="Creator Retention Rate"
            value={users ? `${users.creatorRetentionRate.toFixed(1)}%` : '--'}
            icon={UserCheck}
            loading={usersLoading}
          />
        </div>
      </section>

      {/* Section 3: DTR Token Health */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-muted-foreground">DTR Token Health</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title="Total DTR Staked"
            value={staking ? formatNumber(staking.totalDtrStaked) : '--'}
            icon={Coins}
            loading={stakingLoading}
          />
          <StatsCard
            title="Unique Stakers"
            value={staking ? (staking.uniqueStakers !== null ? formatNumber(staking.uniqueStakers) : 'N/A') : '--'}
            icon={Users}
            loading={stakingLoading}
          />
          <StatsCard
            title="Total SOL Distributed"
            value={staking ? `${formatNumber(staking.totalSolDistributed)} SOL` : '--'}
            icon={Landmark}
            loading={stakingLoading}
          />
          <StatsCard
            title="Staking APY"
            value="Coming Soon"
            icon={Clock}
            loading={false}
          />
        </div>
      </section>

      {/* Section 4: Competitive Positioning */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-muted-foreground">Competitive Positioning</h2>
        <div className="rounded-lg border bg-card p-4 sm:p-6 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand-fg/10 flex-shrink-0">
                <DollarSign className="h-4 w-4 text-brand-fg" />
              </div>
              <div>
                <p className="font-medium text-sm">Creator Earnings</p>
                <p className="text-2xl font-bold">1.4%</p>
                <p className="text-xs text-muted-foreground">of trading volume</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand-fg/10 flex-shrink-0">
                <Zap className="h-4 w-4 text-brand-fg" />
              </div>
              <div>
                <p className="font-medium text-sm">Built-in AI Market Maker</p>
                <p className="text-2xl font-bold">Liquidity Autopilot</p>
                <p className="text-xs text-muted-foreground">automated liquidity management</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand-fg/10 flex-shrink-0">
                <Share2 className="h-4 w-4 text-brand-fg" />
              </div>
              <div>
                <p className="font-medium text-sm">Weekly Revenue Share</p>
                <p className="text-2xl font-bold">50%</p>
                <p className="text-xs text-muted-foreground">to DTR stakers</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand-fg/10 flex-shrink-0">
                <Smartphone className="h-4 w-4 text-brand-fg" />
              </div>
              <div>
                <p className="font-medium text-sm">Mobile Trading App</p>
                <p className="text-2xl font-bold">60,000+</p>
                <p className="text-xs text-muted-foreground">users</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
